/* Re-page server-rendered, customer-visible cards without changing stock filters. */
(function () {
  'use strict';
  if (window.ArtisanVisiblePagination) return;
  const selector = '[data-artisan-visible-config]';
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  const pageNumber = value => Math.max(1, Number.parseInt(value, 10) || 1);

  function contextUrl(href) {
    const url = new URL(href, window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.hash = '';
    return url.href;
  }

  function cardsFrom(root, remote) {
    const grid = root.querySelector('[data-productgrid-items]');
    if (!grid) {
      if (root.querySelector('.productgrid--no-results')) return [];
      throw new Error('Missing product grid');
    }
    const productData = new Map();
    grid.querySelectorAll('script.bold-product-json').forEach(node => {
      const product = JSON.parse(node.textContent);
      productData.set(String(product.id), product);
    });
    return Array.from(grid.children).filter(node => node.matches('[data-product-item]')).map(card => {
      const id = card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      if (!id || (remote && !productData.has(id))) throw new Error('Incomplete product card');
      return {id, card, product: productData.get(id), remote};
    });
  }

  class VisiblePagination {
    constructor(configNode) {
      this.config = JSON.parse(configNode.textContent);
      this.root = configNode.closest('.shopify-section');
      this.initialUrl = window.location.href;
      this.context = contextUrl(this.initialUrl);
      this.abort = new AbortController();
      this.pageCache = new Map();
      this.records = [];
      this.current = 1;
      this.pages = 1;
      this.root.__artisanVisiblePagination = this;
      this.stop = () => this.abort.abort();
      window.addEventListener('pagehide', this.stop, {once: true});
    }

    async fetchPage(page) {
      const url = new URL(this.initialUrl);
      url.searchParams.set('page', page);
      url.searchParams.set('section_id', this.config.sectionId);
      url.searchParams.delete('sections');
      url.hash = '';
      const response = await fetch(url.href, {
        credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal,
      });
      if (!response.ok) throw new Error('Collection request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const configNode = doc.querySelector(selector);
      if (!configNode) throw new Error('Missing collection context');
      const config = JSON.parse(configNode.textContent);
      for (const key of ['sectionId', 'collectionId', 'customerContext', 'pageCount', 'pageSize', 'itemCount']) {
        if (config[key] !== this.config[key]) throw new Error('Collection context changed');
      }
      if (config.currentPage !== page) throw new Error('Unexpected source page');
      const cards = cardsFrom(configNode.closest('.shopify-section') || doc, true);
      this.pageCache.set(page, cards);
    }

    async themeReady() {
      for (let attempt = 0; attempt < 120; attempt++) {
        if (this.abort.signal.aborted) throw new Error('Navigation ended');
        if (this.root.dataset.artisanGridBridge === '1') return;
        await pause(100);
      }
      throw new Error('Theme not ready');
    }

    async preparePrices() {
      const products = this.records.filter(record => record.remote).map(record => record.product);
      if (!products.length || !document.querySelector('#bold-platform-data')) return;
      for (let attempt = 0; attempt < 120; attempt++) {
        if (this.abort.signal.aborted) throw new Error('Navigation ended');
        if (typeof window.BOLD?.pre?.ready === 'function') {
          const api = await window.BOLD.pre.ready();
          if (typeof api.addProductsJson !== 'function') throw new Error('Pricing not ready');
          await api.addProductsJson(products);
          return;
        }
        await pause(100);
      }
      throw new Error('Pricing not ready');
    }

    async start() {
      const config = this.config;
      if (window.Shopify?.designMode || config.pageCount < 2) {
        this.root.dataset.artisanPagination = 'native';
        return;
      }
      if (config.pageCount > 40 || config.pageSize < 1) {
        this.root.dataset.artisanPagination = 'native';
        return;
      }
      this.root.dataset.artisanPagination = 'loading';
      const deadline = setTimeout(() => this.abort.abort(), 30000);
      try {
        this.pageCache.set(config.currentPage, cardsFrom(this.root, false));
        const pending = [];
        for (let page = 1; page <= config.pageCount; page++) {
          if (page !== config.currentPage) pending.push(page);
        }
        // Limit concurrent requests, and keep the native page usable while loading.
        await Promise.all(Array.from({length: Math.min(3, pending.length)}, async () => {
          while (pending.length && !this.abort.signal.aborted) await this.fetchPage(pending.shift());
        }));
        const seen = new Set();
        for (let page = 1; page <= config.pageCount; page++) {
          const cards = this.pageCache.get(page);
          if (!cards) throw new Error('Incomplete collection');
          for (const record of cards) {
            if (seen.has(record.id)) throw new Error('Collection changed during loading');
            seen.add(record.id);
            this.records.push(record);
          }
        }
        if (this.records.length === config.itemCount) {
          this.root.dataset.artisanPagination = 'native';
          return;
        }
        await this.themeReady();
        await this.preparePrices();
        if (this.abort.signal.aborted || contextUrl(window.location.href) !== this.context) {
          throw new Error('Navigation changed');
        }
        this.pages = Math.max(1, Math.ceil(this.records.length / config.pageSize));
        this.render(pageNumber(new URL(window.location.href).searchParams.get('page')), 'replace');
        this.onClick = event => {
          const link = event.target.closest?.('a[data-artisan-visible-page]');
          if (!link || !this.root.contains(link) || event.defaultPrevented || event.button !== 0 ||
              event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.render(Number(link.dataset.artisanVisiblePage), 'push');
          this.root.querySelector('[data-productgrid-outer]')?.scrollIntoView({block: 'start'});
        };
        this.onPopState = () => {
          if (contextUrl(window.location.href) !== this.context) return;
          this.render(pageNumber(new URL(window.location.href).searchParams.get('page')), null);
        };
        this.root.addEventListener('click', this.onClick);
        window.addEventListener('popstate', this.onPopState);
        this.root.dataset.artisanPagination = 'ready';
      } catch (error) {
        this.root.dataset.artisanPagination = 'native-fallback';
        this.abort.abort();
        // Failed enhancement leaves all original cards and pagination in place.
        console.warn('Collection pagination retained its original layout.');
      } finally {
        clearTimeout(deadline);
      }
    }

    pageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      if (page === 1) url.searchParams.delete('page');
      else url.searchParams.set('page', page);
      return url.pathname + url.search + url.hash;
    }

    pagination(page) {
      if (this.pages === 1) return null;
      const nav = document.createElement('nav');
      nav.className = 'pagination--container';
      nav.setAttribute('aria-label', 'Pagination');
      const list = document.createElement('ul');
      list.className = 'pagination--inner';
      nav.append(list);
      const addLink = (number, text, className) => {
        const item = document.createElement('li');
        item.className = className || '';
        const link = document.createElement('a');
        link.className = 'pagination--item';
        link.href = this.pageUrl(number);
        link.dataset.artisanVisiblePage = number;
        link.setAttribute('aria-label', `Go to page ${number}`);
        link.textContent = text;
        item.append(link);
        list.append(item);
      };
      if (page > 1) addLink(page - 1, `‹ ${this.config.previousLabel}`, 'pagination--previous');
      let last = 0;
      for (let number = 1; number <= this.pages; number++) {
        if (number !== 1 && number !== this.pages && Math.abs(number - page) > 2) continue;
        if (last && number > last + 1) {
          const gap = document.createElement('li');
          gap.className = 'pagination--ellipsis';
          gap.innerHTML = '<span class="pagination--item">…</span>';
          list.append(gap);
        }
        if (number === page) {
          const item = document.createElement('li');
          item.className = 'pagination--active';
          item.setAttribute('aria-current', 'page');
          item.setAttribute('aria-label', `Page ${page}`);
          const label = document.createElement('span');
          label.className = 'pagination--item';
          label.textContent = page;
          item.append(label);
          list.append(item);
        } else addLink(number, number);
        last = number;
      }
      if (page < this.pages) addLink(page + 1, `${this.config.nextLabel} ›`, 'pagination--next');
      return nav;
    }

    render(requestedPage, historyMode) {
      const page = Math.min(this.pages, Math.max(1, requestedPage));
      const selected = this.records.slice((page - 1) * this.config.pageSize, page * this.config.pageSize);
      const wrapper = this.root.querySelector('.productgrid--wrapper');
      if (!wrapper) throw new Error('Missing collection container');
      let grid = this.root.querySelector('[data-productgrid-items]');
      const oldGrid = grid;
      if (!grid) {
        grid = document.createElement('ul');
        grid.className = `productgrid--items products-per-row-${this.config.columns}`;
        grid.setAttribute('data-productgrid-items', '');
        grid.setAttribute('data-html', 'productgrid-items');
      }
      const oldChildren = Array.from(grid.childNodes);
      const oldNav = wrapper.querySelector('.pagination--container');
      const oldEmpty = wrapper.querySelector('.productgrid--no-results');
      const newNav = this.pagination(page);
      const newEmpty = document.createElement('div');
      newEmpty.className = 'productgrid--no-results';
      const emptyTitle = document.createElement('h2');
      emptyTitle.className = 'productgrid--no-results-title';
      emptyTitle.textContent = this.config.emptyLabel;
      newEmpty.append(emptyTitle);
      const detail = {
        applied: false,
        rollback: () => {
          grid.replaceChildren(...oldChildren);
          if (newNav) newNav.remove();
          newEmpty.remove();
          if (oldNav && !oldNav.isConnected) grid.after(oldNav);
          if (oldEmpty && !oldEmpty.isConnected) grid.after(oldEmpty);
          if (!oldGrid) grid.remove();
        },
        apply: () => {
          if (!oldGrid) {
            if (oldEmpty) oldEmpty.before(grid);
            else if (oldNav) oldNav.before(grid);
            else wrapper.append(grid);
          }
          grid.replaceChildren(...selected.map(record => record.card));
          if (oldEmpty) oldEmpty.remove();
          if (!selected.length) grid.after(newEmpty);
          if (oldNav) {
            if (newNav) oldNav.replaceWith(newNav);
            else oldNav.remove();
          } else if (newNav) grid.after(newNav);
        },
      };
      this.root.dispatchEvent(new CustomEvent('artisan:collection-grid:commit', {detail}));
      if (!detail.applied) {
        detail.rollback();
        throw new Error('Theme could not refresh cards');
      }
      this.current = page;
      this.root.dataset.artisanVisibleCount = this.records.length;
      if (historyMode) {
        const url = this.pageUrl(page);
        const currentUrl = window.location.pathname + window.location.search + window.location.hash;
        if (url !== currentUrl) window.history[historyMode + 'State'](window.history.state, '', url);
      }
      if (window.Shopify?.queryParams) {
        if (page === 1) delete window.Shopify.queryParams.page;
        else window.Shopify.queryParams.page = String(page);
      }
    }
  }

  function boot() {
    document.querySelectorAll(selector).forEach(node => {
      if (node.dataset.artisanStarted) return;
      node.dataset.artisanStarted = '1';
      try { new VisiblePagination(node).start(); } catch (error) { /* Keep native navigation. */ }
    });
  }
  window.ArtisanVisiblePagination = {boot, version: '1.0.0'};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
})();
